import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import QuoteForm from '../../components/base/QuoteForm';

const HomePage = () => {
  useEffect(() => {
    // Update last-modified meta tag
    const lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) {
      lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
    }
  }, []);

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section - Redesigned */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/738836373d10d8ba5a75b80b5e54b7d5.jpeg)'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        
        <div className="container mx-auto px-4 relative z-10 py-32">
          <div className="max-w-5xl mx-auto">
            {/* Subtle Trust Badge */}
            <div className="flex items-center justify-center gap-3 mb-8 animate-fade-in">
              <div className="flex items-center gap-1">
                <i className="ri-star-fill text-yellow-400 text-xl drop-shadow-lg"></i>
                <i className="ri-star-fill text-yellow-400 text-xl drop-shadow-lg"></i>
                <i className="ri-star-fill text-yellow-400 text-xl drop-shadow-lg"></i>
                <i className="ri-star-fill text-yellow-400 text-xl drop-shadow-lg"></i>
                <i className="ri-star-fill text-yellow-400 text-xl drop-shadow-lg"></i>
              </div>
              <span className="text-white text-lg font-semibold drop-shadow-lg">4.9/5 from 500+ Homeowners in Northwest Arkansas</span>
            </div>

            {/* Main Headline */}
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white text-center mb-6 leading-tight drop-shadow-2xl animate-fade-in">
              Never Clean Your<br />Gutters Again
            </h2>

            {/* Sub-headline with Benefits */}
            <p className="text-xl md:text-2xl text-white text-center mb-12 max-w-3xl mx-auto leading-relaxed drop-shadow-lg animate-fade-in">
              Lifetime warranty stone-coated protection. Professional installation in one day.<br />
              <strong className="text-yellow-400">100% clog-free guarantee</strong> or your money back.
            </p>

            {/* Dual CTA Strategy */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-16 animate-slide-up">
              {/* Primary CTA - Book Appointment */}
              <Link 
                to="/contact"
                className="group relative bg-gradient-to-r from-orange-500 to-orange-600 text-white px-10 py-6 rounded-xl text-xl font-bold hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-2xl hover:shadow-orange-500/50 hover:scale-105 touch-manipulation whitespace-nowrap w-full sm:w-auto"
              >
                <span className="flex items-center justify-center">
                  <i className="ri-calendar-check-line mr-3 text-2xl"></i>
                  Schedule Free Inspection
                </span>
                <span className="block text-sm font-normal mt-1 opacity-90">Get quote in 24 hours</span>
              </Link>

              {/* Secondary CTA - Call Now */}
              <a 
                href="tel:479-337-7565"
                className="group bg-white/95 backdrop-blur-sm text-gray-900 px-10 py-6 rounded-xl text-xl font-bold hover:bg-white transition-all duration-300 shadow-2xl hover:scale-105 touch-manipulation whitespace-nowrap w-full sm:w-auto"
              >
                <span className="flex items-center justify-center">
                  <i className="ri-phone-fill mr-3 text-2xl text-green-600"></i>
                  (479) 337-7565
                </span>
                <span className="block text-sm font-normal mt-1 text-gray-600">Talk to an expert now</span>
              </a>
            </div>

            {/* Value Props - Cleaner Design */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto animate-scale-in">
              <div className="bg-white/10 backdrop-blur-md border-2 border-white/30 rounded-xl p-6 text-center hover:bg-white/20 transition-all">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <i className="ri-shield-check-line text-3xl text-white"></i>
                </div>
                <p className="text-white font-bold text-lg mb-2">Lifetime Warranty</p>
                <p className="text-white/90 text-sm">Strongest guarantee in Arkansas</p>
              </div>

              <div className="bg-white/10 backdrop-blur-md border-2 border-white/30 rounded-xl p-6 text-center hover:bg-white/20 transition-all">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <i className="ri-timer-flash-line text-3xl text-white"></i>
                </div>
                <p className="text-white font-bold text-lg mb-2">One-Day Install</p>
                <p className="text-white/90 text-sm">Most jobs done same day</p>
              </div>

              <div className="bg-white/10 backdrop-blur-md border-2 border-white/30 rounded-xl p-6 text-center hover:bg-white/20 transition-all">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <i className="ri-hand-coin-line text-3xl text-white"></i>
                </div>
                <p className="text-white font-bold text-lg mb-2">Flexible Financing</p>
                <p className="text-white/90 text-sm">Options for every budget</p>
              </div>
            </div>

            {/* Urgency Message */}
            <div className="text-center mt-12 animate-fade-in">
              <p className="text-white/90 text-base">
                <i className="ri-time-line mr-2"></i>
                <strong>Limited slots available</strong> — Book your free inspection this week
              </p>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white text-3xl drop-shadow-lg"></i>
        </div>
      </section>

      {/* Trust Bar - Social Proof */}
      <section className="bg-gradient-to-r from-green-600 to-green-700 py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 text-white text-center">
            <div className="flex items-center gap-3">
              <i className="ri-shield-check-line text-4xl"></i>
              <div className="text-left">
                <div className="text-2xl font-bold">10+</div>
                <div className="text-sm opacity-90">Years Experience</div>
              </div>
            </div>
            <div className="hidden md:block w-px h-12 bg-white/30"></div>
            <div className="flex items-center gap-3">
              <i className="ri-home-smile-line text-4xl"></i>
              <div className="text-left">
                <div className="text-2xl font-bold">500+</div>
                <div className="text-sm opacity-90">Homes Protected</div>
              </div>
            </div>
            <div className="hidden md:block w-px h-12 bg-white/30"></div>
            <div className="flex items-center gap-3">
              <i className="ri-star-fill text-4xl text-yellow-400"></i>
              <div className="text-left">
                <div className="text-2xl font-bold">4.9/5</div>
                <div className="text-sm opacity-90">Customer Rating</div>
              </div>
            </div>
            <div className="hidden md:block w-px h-12 bg-white/30"></div>
            <div className="flex items-center gap-3">
              <i className="ri-award-fill text-4xl"></i>
              <div className="text-left">
                <div className="text-2xl font-bold">A+</div>
                <div className="text-sm opacity-90">BBB Rated</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 animate-fade-in">
            <h3 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              Professional Gutter Services
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete gutter solutions to protect your home from water damage and foundation issues
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-shield-star-line',
                title: 'Stone Coated Gutter Cap',
                description: 'Premium protection with our flagship system. Hail-resistant design with lifetime warranty.',
                link: '/services/stone-coated-gutter-cap',
                badge: 'Most Popular'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Premium Gutter Guards',
                description: 'High-quality protection to keep debris out and water flowing smoothly year-round.',
                link: '/services/premium-gutter-guards',
                badge: null
              },
              {
                icon: 'ri-tools-line',
                title: 'Gutter Guard Installation',
                description: 'Expert installation of various systems tailored to your home and budget.',
                link: '/services/gutter-guard-installation',
                badge: null
              },
              {
                icon: 'ri-water-flash-line',
                title: 'Seamless Gutters',
                description: 'Custom-fitted systems that eliminate leaks and enhance your home\'s appearance.',
                link: '/services/seamless-gutters',
                badge: null
              },
              {
                icon: 'ri-brush-line',
                title: 'Gutter Cleaning',
                description: 'Thorough cleaning to maintain optimal performance and prevent costly damage.',
                link: '/services/gutter-cleaning',
                badge: null
              },
              {
                icon: 'ri-drop-line',
                title: 'French Drains',
                description: 'Professional drainage solutions to redirect water away from your foundation.',
                link: '/services/french-drains',
                badge: null
              }
            ].map((service, index) => (
              <Link 
                key={index}
                to={service.link}
                className="block"
              >
                <article 
                  className="bg-white border-2 border-gray-100 rounded-xl p-8 hover:border-green-600 hover:shadow-2xl transition-all duration-300 group relative animate-slide-up h-full"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {service.badge && (
                    <div className="absolute -top-3 right-6 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                      {service.badge}
                    </div>
                  )}
                  <div className="w-16 h-16 bg-gradient-to-br from-green-50 to-green-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <i className={`${service.icon} text-3xl text-green-600`}></i>
                  </div>
                  <h4 className="text-2xl font-bold mb-4 text-gray-900 group-hover:text-green-600 transition-colors">
                    {service.title}
                  </h4>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <span className="inline-flex items-center text-green-600 font-semibold group-hover:text-green-700 transition-colors">
                    Learn More 
                    <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                  </span>
                </article>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <article>
              <h3 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Why Northwest Arkansas Homeowners Choose Us
              </h3>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Since 2015, we've protected over 500 homes across Fayetteville, Rogers, and Bentonville with premium gutter solutions. Our commitment to quality and customer satisfaction has made us the area's most trusted gutter specialists.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-lg flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      Lifetime Warranty Protection
                    </h4>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Every installation includes our industry-leading lifetime warranty. We stand behind our work with the strongest guarantee in Northwest Arkansas.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-lg flex-shrink-0">
                    <i className="ri-hammer-line text-2xl text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      Weather-Resistant Design
                    </h4>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Engineered for Arkansas weather. Our hail-resistant systems withstand severe storms and protect your home year-round.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-lg flex-shrink-0">
                    <i className="ri-drop-line text-2xl text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      100% Clog-Free Guarantee
                    </h4>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Our advanced micro-mesh technology keeps out leaves, pine needles, and debris while allowing maximum water flow.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      Local Family-Owned Business
                    </h4>
                    <p className="text-base text-gray-600 leading-relaxed">
                      We understand local weather patterns and provide personalized service you can trust. Your neighbors rely on us—so can you.
                    </p>
                  </div>
                </div>
              </div>
            </article>

            <div className="relative">
              <img 
                src="https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/fadab8b61b9d9ae41f7fc3d11029c274.jpeg"
                alt="Professional gutter guard installation before and after comparison in Northwest Arkansas"
                title="NWA Gutter Guards - Quality Installation Results"
                className="w-full h-auto rounded-lg shadow-xl"
                loading="lazy"
                width="800"
                height="1000"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section with Schema */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h3 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
                Frequently Asked Questions
              </h3>
              <p className="text-xl text-gray-600">
                Get answers to common questions about gutter guards and our services
              </p>
            </div>

            <div className="space-y-6">
              {[
                {
                  question: 'How long do gutter guards last?',
                  answer: 'Our stone coated gutter guards are built to last a lifetime. We back them with a lifetime warranty because they\'re engineered to withstand decades of harsh Northwest Arkansas weather, including hail, heavy rain, and extreme temperatures.'
                },
                {
                  question: 'Will gutter guards work in heavy rain?',
                  answer: 'Yes! Our gutter guard systems are specifically designed to handle Northwest Arkansas\' heavy rainfall. The micro-mesh technology allows maximum water flow while blocking all debris, ensuring your gutters perform perfectly even during severe storms.'
                },
                {
                  question: 'How much do gutter guards cost?',
                  answer: 'The cost varies based on your home\'s size, gutter length, and the system you choose. Most Northwest Arkansas homeowners invest between $1,500-$3,500 for complete protection. We offer free estimates and flexible financing options to fit any budget.'
                },
                {
                  question: 'Do I still need to clean my gutters with guards installed?',
                  answer: 'No! Our gutter guards come with a 100% clog-free guarantee. You\'ll never need to clean your gutters again. We design our systems to keep out leaves, pine needles, and all debris while maintaining perfect water flow.'
                },
                {
                  question: 'How long does installation take?',
                  answer: 'Most installations are completed in one day. Our professional crew works efficiently without disrupting your daily routine. We\'ll provide a specific timeline during your free estimate based on your home\'s size and the system you select.'
                },
                {
                  question: 'Are gutter guards worth the investment?',
                  answer: 'Absolutely! Gutter guards protect your home from water damage, prevent foundation issues, eliminate dangerous ladder climbing, and increase your property value. Most homeowners recoup their investment through reduced maintenance and avoided repair costs.'
                }
              ].map((faq, index) => (
                <details 
                  key={index}
                  className="bg-gray-50 rounded-lg border-2 border-gray-200 hover:border-green-600 transition-colors"
                >
                  <summary className="p-6 cursor-pointer font-bold text-lg text-gray-900 flex items-center justify-between">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-2xl text-green-600"></i>
                  </summary>
                  <div className="px-6 pb-6 text-gray-700 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>

            <div className="text-center mt-12">
              <p className="text-lg text-gray-600 mb-4">Have more questions?</p>
              <Link 
                to="/contact"
                className="inline-flex items-center text-green-600 font-bold text-lg hover:text-green-700"
              >
                Contact us for personalized answers
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>

        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How long do gutter guards last?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our stone coated gutter guards are built to last a lifetime. We back them with a lifetime warranty because they're engineered to withstand decades of harsh Northwest Arkansas weather, including hail, heavy rain, and extreme temperatures."
                }
              },
              {
                "@type": "Question",
                "name": "Will gutter guards work in heavy rain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes! Our gutter guard systems are specifically designed to handle Northwest Arkansas' heavy rainfall. The micro-mesh technology allows maximum water flow while blocking all debris, ensuring your gutters perform perfectly even during severe storms."
                }
              },
              {
                "@type": "Question",
                "name": "How much do gutter guards cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The cost varies based on your home's size, gutter length, and the system you choose. Most Northwest Arkansas homeowners invest between $1,500-$3,500 for complete protection. We offer free estimates and flexible financing options to fit any budget."
                }
              },
              {
                "@type": "Question",
                "name": "Do I still need to clean my gutters with guards installed?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No! Our gutter guards come with a 100% clog-free guarantee. You'll never need to clean your gutters again. We design our systems to keep out leaves, pine needles, and all debris while maintaining perfect water flow."
                }
              },
              {
                "@type": "Question",
                "name": "How long does installation take?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most installations are completed in one day. Our professional crew works efficiently without disrupting your daily routine. We'll provide a specific timeline during your free estimate based on your home's size and the system you select."
                }
              },
              {
                "@type": "Question",
                "name": "Are gutter guards worth the investment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely! Gutter guards protect your home from water damage, prevent foundation issues, eliminate dangerous ladder climbing, and increase your property value. Most homeowners recoup their investment through reduced maintenance and avoided repair costs."
                }
              }
            ]
          })}
        </script>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              What Homeowners Are Saying
            </h3>
            <p className="text-xl text-gray-600">Real reviews from satisfied customers across Northwest Arkansas</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                name: 'Sarah Mitchell',
                location: 'Bentonville, AR',
                rating: 5,
                text: 'Best investment we made for our home! The installation was professional and the results are amazing. No more cleaning gutters every fall.',
                service: 'Stone Coated Gutter Cap'
              },
              {
                name: 'Mike Thompson',
                location: 'Rogers, AR',
                rating: 5,
                text: 'Professional crew, fair pricing, and excellent quality. They completed the installation in one day. Highly recommend!',
                service: 'Seamless Gutters'
              },
              {
                name: 'Jennifer Davis',
                location: 'Fayetteville, AR',
                rating: 5,
                text: 'After years of clogged gutters, these guards are a game changer. The lifetime warranty gives us peace of mind. Worth every penny!',
                service: 'Premium Gutter Guards'
              }
            ].map((testimonial, index) => (
              <article 
                key={index} 
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 animate-scale-in"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-500 text-xl"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">
                  "{testimonial.text}"
                </p>
                <div className="border-t border-gray-100 pt-4">
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.location}</p>
                  <p className="text-sm text-green-600 font-semibold mt-2">{testimonial.service}</p>
                </div>
              </article>
            ))}
          </div>

          <div className="text-center mt-12">
            <div className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full shadow-lg">
              <i className="ri-google-fill text-2xl text-blue-600"></i>
              <span className="font-semibold text-gray-900">4.9/5 on Google</span>
              <span className="text-gray-600">•</span>
              <span className="text-gray-600">150+ Reviews</span>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <QuoteForm />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-green-700 via-green-800 to-green-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
            Ready to Protect Your Home?
          </h3>
          <p className="text-xl lg:text-2xl mb-10 max-w-3xl mx-auto opacity-95 leading-relaxed">
            Get a free estimate and discover why 500+ homeowners trust us with their gutter protection
          </p>
          <Link 
            to="/contact" 
            className="inline-flex items-center justify-center px-10 py-5 bg-orange-500 hover:bg-orange-600 text-white text-xl font-bold rounded-lg shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 touch-manipulation whitespace-nowrap"
          >
            Request Free Quote <i className="ri-arrow-right-line ml-3 text-2xl"></i>
          </Link>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-3xl lg:text-4xl font-bold mb-6 leading-tight">
            Serving All of Northwest Arkansas
          </h3>
          <p className="text-lg mb-8 max-w-3xl mx-auto leading-relaxed">
            Professional gutter protection services throughout the region
          </p>
          <nav aria-label="Service areas">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 text-base">
              <div className="bg-white/10 rounded-lg p-5 min-h-[60px] flex items-center justify-center">
                <strong>Fayetteville</strong>
              </div>
              <div className="bg-white/10 rounded-lg p-5 min-h-[60px] flex items-center justify-center">
                <strong>Springdale</strong>
              </div>
              <div className="bg-white/10 rounded-lg p-5 min-h-[60px] flex items-center justify-center">
                <strong>Rogers</strong>
              </div>
              <div className="bg-white/10 rounded-lg p-5 min-h-[60px] flex items-center justify-center">
                <strong>Bentonville</strong>
              </div>
              <div className="bg-white/10 rounded-lg p-5 min-h-[60px] flex items-center justify-center">
                <strong>Bella Vista</strong>
              </div>
              <div className="bg-white/10 rounded-lg p-5 min-h-[60px] flex items-center justify-center">
                <strong>Siloam Springs</strong>
              </div>
            </div>
          </nav>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
